import { flattenMessages } from '../flattenMessages';

export default flattenMessages({
  common: {
    buttons: {
      add: {
        label: 'Add',
      },
    },
    saveChanges: 'Save changes',
    save: 'Save',
    cancel: 'Cancel',
    submit: 'Submit',
    confirm: 'Confirm',
    weekDays: {
      sunday: 'Sunday',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
    },
    greeting: 'Hello, {name}!',
    clear: 'Clear',
    applyFilters: 'Apply filters',
    back: 'Back',
    next: 'Next',
    search: 'Search',
    filter: 'Filter',
    all: 'All',
    name: 'Name',
    clearAllFilters: 'Clear all filters',
    byPerson: 'by {name}',
    actions: 'Actions',
    assignDialog: {
      title: 'Assign',
      placeholder: 'Please, specify the value',
    },
    status: 'Status',
    location: 'Location',
    yes: 'Yes',
    no: 'No',
    unknownError: 'Something went wrong',
    view: 'View',
    edit: 'Edit',
    deleteImage: 'Delete image',
    fileValidationError: 'Wrong file format: image should be PNG or JPG format, {sizeMb}MB max.',
    copyingSuccessful: 'Copied',
  },
  molecules: {
    attachmentUpload: {
      upload: 'Click to upload',
      subUpload: 'or drag and drop',
      helper: 'JPEG, PNG or PDF (max. {sizeMb}MB)',
      delete: 'Delete',
      view: {
        label: 'View timecard PDF',
        tooltip: 'Download the attached PDF file',
      },
      gotAttachment: {
        label: 'PDF file is attached',
        tooltip: 'Please save the changes to see the file',
      },
    },
  },
  system: {
    newVersionLabel: {
      message: {
        title: 'A new update is available. Please reload the page to apply the latest changes.',
      },
      refreshButton: {
        label: 'Refresh',
      },
    },
  },
  reportAnIssue: {
    dialog: {
      title: 'Report an issue',
      reporterLabel: 'Reporter: ',
      logGrabbedMessage: 'Log already collected and will be sent with your report. ',
      logGrabbedTooltip:
        'These logs include only technical data related to your activity in the ShiftNex at the time of the issue (no personal information is collected). They help our support team reproduce and resolve your problem faster',
      logGrabbedCheckboxLabel: 'I agree to share ShiftNex technical logs to help investigate this issue.',
      issueDescriptionLabel: 'Please, describe your issue below:',
      successMessage: 'Your issue report has been successfully sent. Thank you for helping us improve the platform!',
      errorMessage: 'An error occurred while sending your issue report. Please try again later.',
      actions: {
        cancel: 'Cancel',
        submit: 'Submit',
      },
    },
  },
  auth: {
    login: {
      title: 'Login',
      subtitle: 'Please, provide your email or phone number to receive an OTP',
      tabEmailTitle: 'Email',
      tabPhoneTitle: 'Phone number',
      emailPlaceholder: 'e.g. jhondoe@gmail.com',
      signUpText: 'Don’t have a ShiftNex account yet? <link>Sign Up</link>',
    },
    tenant: {
      title: 'Login',
      subtitle: 'Please, choose an organization you want to login to',
      tenantInput: {
        label: 'Organization',
        placeholder: 'Organization',
      },
      continueButton: {
        label: 'Continue',
      },
    },
    otp: {
      title: 'Secure verification',
      subtitle: 'We’ve just sent you a temporary 6-digit code verification. Please check email and/or phone.',
      inputPlaceholder: 'Enter the code',
      resendCode: 'Resend the code',
      resendCodeSuccess: 'Code successfully resent',
    },
    adminSignUp: {
      title: 'Welcome to ShiftNex!',
      titleWithName: '{name}, welcome to ShiftNex!',
      subtitle: 'Join the ShiftNex Healthcare Platform and enjoy the way we manage healthcare staffing operations',
      otpBySmsLabel: 'Receive OTP by sms',
      otpBySmsInstructions: 'Please, fill in your phone number if you want receive the one-time password  by SMS',
      submitLabel: 'Create account',
    },
    adminSignUpDetails: {
      title: 'Agency',
      subtitle: 'Please, fill in your agency details.',
    },
    adminSignUpAddress: {
      title: 'Address',
      subtitle: 'Please, fill in your agency address details.',
    },
    footer: {
      legalMessage:
        'By using {name} you agree to the <termsLink>Terms of Service</termsLink> and <privacyLink>Privacy Policy</privacyLink>',
      version: 'Version {version}',
    },
    submitLabel: 'Continue',
    signInText: 'Already have a ShiftNex account? <link>Sign In</link>',
  },
  sidebar: {
    poweredBy: 'Powered by ShiftNex',
    dashboard: 'Dashboard',
    clients: 'Clients',
    shifts: 'Shifts',
    talent: 'Providers',
    attendance: 'Attendance',
    contacts: 'Contacts',
    marketplace: 'Marketplace',
    settings: 'Settings',
    billing: 'Billing',
    help: 'Help',
  },
  onboarding: {
    progress: {
      gettingStarted: 'Getting started',
      menuHeader: 'Onboarding',
    },
    organization: {
      title: 'Organization details',
      subtitle: 'Fill in your organization details',
      subtitleLong: 'Fill in basic information about your organization',
    },
    users: {
      title: 'Users',
      titleAlternative: 'Users',
      subtitle: 'Add users and set up the roles',
      subtitleLong: 'Create and invite system users of your organization',
    },
    qualifications: {
      title: 'Positions & Specialties',
      titleAlternative: 'Positions & Specialties',
      subtitle: 'Set up positions and specialties used in your organization.',
      subtitleLong:
        'Set up positions and specialties used in your organization. You can select them from default system list or create custom ones',
    },
    locations: {
      title: 'Locations',
      titleAlternative: 'Locations',
      subtitle: 'Here you can add and manage your organization’s locations',
      subtitleLong: 'Here you can add and manage your organization’s locations',
    },
    providers: {
      title: 'Providers',
      titleAlternative: 'Providers',
      subtitle: 'Add healthcare providers so you can assign them to shifts',
      subtitleLong: 'Add healthcare providers so you can assign them to shifts',
    },
    clients: {
      title: 'Clients',
      subtitle: 'Set up the relationship with facilities',
      subtitleLong:
        'View the facilities available on ShiftNex platform. Once they share shifts with you, you can propose your providers for assignment',
    },
    shifts: {
      title: 'Shifts',
      subtitle: 'Create and assign shifts',
      subtitleLong:
        'View the facilities available on ShiftNex platform. Once they share shifts with you, you can propose your providers for assignment',
    },
    widget: {
      hide: 'Hide',
      show: 'Show Onboarding',
      previousStep: 'Previous step',
      nextStep: 'Next step',
      addDetails: 'Add details',
      setBranding: 'Set branding',
      getStarted: 'Get started',
    },
  },
  license: {
    trial: {
      daysLeft: '{days} days left in trial',
      upgradeBtnLabel: 'Upgrade',
    },
  },
  header: {
    refresh: {
      label: 'Refresh',
    },
    manageAccount: 'Manage account',
    profileMenu: {
      pauseNotificationsLabel: 'Pause notifications',
      profileSettingsLabel: 'Profile settings',
      supportLabel: 'Support chat',
      reportLabel: 'Report an issue',
      logOutLabel: 'Log out',
    },
    notificationSchedule: {
      label: 'Pause notifications for...',
      postponeMins: 'For {value, plural, one {# minute} other {# minutes}}',
      postponeHours: 'For {value, plural, one {# hour} other {# hours}}',
      postponeTomorrow: 'Until tomorrow',
    },
  },
  comingSoon: {
    title: 'Coming soon...',
    description: 'The page is not yet available but we are working on it',
  },
  settings: {
    menu: {
      general: {
        title: 'General',
      },
      organization: {
        title: 'Organization',
      },
      rolesAndUsers: {
        title: 'Roles and users',
      },
      groups: {
        title: 'Access groups',
      },
      users: {
        title: 'System users',
      },
      privacyAndSecurity: {
        title: 'Privacy and security',
      },
      organizationDetails: {
        title: 'Organization details',
      },
      documents: {
        title: 'Documents',
      },
      branding: {
        title: 'Branding',
      },
      qualificationsSpecialties: {
        title: 'Positions & Specialties',
      },
      qualifications: {
        title: 'Positions',
      },
      specialties: {
        title: 'Specialties',
      },
      locations: {
        title: 'Locations',
      },
    },
    organization: {
      savedSuccessfully: 'Organization details saved successfully',
      title: 'Organization details',
      subtitle: 'Please fill in your organization information so the ShiftNex can proceed with verifying your account',
      mainDetails: {
        title: 'Main details',
        subtitle: 'Please, fill in organization contact details and EIN',
      },
      address: {
        title: 'Address details',
        subtitle: 'Please, fill in your agency address details.',
      },
      contact: {
        title: 'Main organization contact',
        subtitle: 'Please, fill in contact details',
      },
      operational: {
        title: 'Operational setup',
        subtitle: 'Set up the way organization operates',
      },
    },
    qualSpeck: {
      qualifications: {
        title: 'Positions',
        subtitle:
          'In this section you can add positions used in your organization. You can select default values provided by the system or create your custom ones. Please note that position is mandatory for all users. Also, system default entities cannot be edited or deleted.',

        directQualBox: {
          title: 'Clinical',
          titleInfoTooltip: 'This category contains positions that can be applied to your providers',
          emptyListPlaceholderText: 'No positions inside "Nurses" category',
          editQualListButtonLabel: 'Edit positions',
        },
        editDirectQalDialog: {
          dialogHeading: 'Clinical',
          listCounter: '{amount} positions',
          addButton: 'Add position',
          selectTitle: 'Position',
          selectPlaceholder: 'Position',
          abbreviationTitle: 'Abbreviation',
          noAbbreviationLabel: 'no position chosen',
        },
        otherQualBox: {
          title: 'Other',
          titleInfoTooltip: 'In this category you can specify positions for your system users',
          emptyListPlaceholderText: 'No positions inside "Other" category',
          editQualListButtonLabel: 'Edit positions',
        },
        editOtherQalDialog: {
          dialogHeading: 'Other',
          listCounter: '{amount} positions',
          addButton: 'Add position',
          selectTitle: 'Position',
          abbreviationTitle: 'Abbreviation',
        },
        errors: {
          QualificationInUseBySpecialty:
            'This position is used for one or multiple entities of your organization and cannot be deleted',
          NurseQualificationInUseByInternalStaff:
            'This position is used for one or multiple entities of your organization and cannot be deleted',
          OtherQualificationInUseByDepartment: 'Non-clinical position is in use by one or more departments',
          NurseQualificationInUseByShift: 'This position is used for one or multiple shifts and cannot be deleted',
          NurseQualificationInUseByDepartment:
            'This position is used for one or multiple entities of your organization and cannot be deleted',
          NurseQualificationInUseByExternalStaff:
            'This position is used for one or multiple entities of your organization and cannot be deleted',
        },
      },
      specialties: {
        title: 'Specialties',
        subtitle:
          'Here you can add specialties of your facility. Each specialty should be mapped to one or multiple positions.',
        specialtiesCounter: '{amount} Specialties',
        addSpecialtyButtonLabel: 'Add specialty',
        table: {
          noRows: {
            title: 'No specialties yet',
            subtitle: "You don't have any specialties yet",
          },
          columns: {
            specialty: {
              heading: 'Specialty',
            },
            qualification: {
              heading: 'Position',
            },
          },
        },
        editSpecialtyDialog: {
          heading: 'Edit Specialty',
          selectTitle: 'Specialty title',
          selectTitlePlaceholder: 'Please select specialty',
          directQualificationsSectHeading: 'Clinical positions',
          otherQualificationsSectHeading: 'Other positions',
          errors: {
            qualNotSelected: 'Please select at least one position',
          },
        },
        addSpecialtyDialog: {
          heading: 'New Specialty',
          selectTitle: 'Specialty title',
          selectTitlePlaceholder: 'Please select specialty',
          directQualificationsSectHeading: 'Clinical positions',
          otherQualificationsSectHeading: 'Other positions',
          errors: {
            qualNotSelected: 'Please select at least one position',
          },
        },
      },
    },
    users: {
      title: 'System users',
      subtitle: 'Each user will belong to one of the roles below, and different roles have their unique privilege',
      sendInvite: {
        success: 'Invite has been sent successfully',
      },
      actions: {
        resendInvite: 'Resend invite',
        delete: 'Delete',
        deactivate: {
          label: 'Deactivate',
          success: 'User successfully deactivated',
        },
        activate: {
          label: 'Activate',
          success: 'User successfully activated',
        },
      },
      deleteConfirmation: {
        title: 'Delete user',
        content: 'Are you sure you want to delete this user?',
        success: 'User has been deleted successfully',
      },
    },
    locations: {
      listPage: {
        title: 'Locations',
        subtitle:
          'This section displays the list of locations included in your facility. In ‘Structure’ sub-tab you can view and edit your organization hierarchical tree and it’s entities',
        locationsCounter: '{amount} Locations',
        addLocationButtonLabel: 'Add location',
        table: {
          noRows: {
            title: 'No locations yet',
            subtitle: "You don't have any locations yet",
          },
          columns: {
            name: {
              heading: 'Location name',
            },
            region: {
              heading: 'Region',
            },
            contact: {
              heading: 'Main contact',
            },
            email: {
              heading: 'Email',
            },
            status: {
              heading: 'Status',
            },
          },
        },
      },
      newLocationPage: {
        heading: 'New location',
      },
      editPage: {
        backButton: {
          label: 'Back to Locations',
        },
        tabs: {
          general: {
            label: 'General details',
            form: {
              main: {
                heading: 'Main details',
                subtitle: "Enter the location's basic details such as name and contact information.",
                locationName: {
                  label: 'Location name',
                  placeholder: 'Please add a name for location',
                },
                isActive: {
                  label: 'Location is active',
                },
                isHeadquarters: {
                  label: 'Location is a headquarters',
                },
              },
              address: {
                heading: 'Location address',
                subtitle: 'Provide the full address for this location.',
              },
              contact: {
                heading: 'Main location contact',
                subtitle: 'Add the primary contact person’s details for this location.',
                primaryContactName: {
                  label: "Primary contact's full name",
                  placeholder: 'Enter full name',
                },
                contactJobTitle: {
                  label: 'Job title',
                  placeholder: 'Enter job title',
                },
                primaryPhoneNumber: {
                  label: 'Primary phone number',
                  placeholder: 'Enter job title',
                },
                secondaryPhoneNumber: {
                  label: 'Secondary phone number',
                  placeholder: 'Enter job title',
                },
              },
              operational: {
                heading: 'Operational setup',
                subtitle: 'Choose the starting day of the operational week for this location.',
              },
            },
          },
          structure: {
            label: 'Location structure',
          },
        },
      },
    },
  },
  forms: {
    requiredFieldsMissing: '{number} mandatory fields missing',
    fieldIsRequired: 'This field is required',
    maxLengthError: 'Max length is {number}',
    minLengthError: 'Min length is {number}',
    invalidPhoneNumber: 'Please, enter valid phone number',
    invalidEIN: 'Please, check the format of the input',
    invalidZipCode: 'Please, enter valid zip code',
    invalidEmail: 'Please, enter valid email',
    emptyLinesOfBusiness: 'Select at least one business line of the organization',
    emptyStatesOfService: 'Select at least one state',
    invalidWebsite: 'Please, enter valid website',
    invalidTime: 'Non valid time',
    invalidCityAndState: 'Compatible format is "Chicago, IL"',
    invalidStateLicenseNumber: 'Please, enter valid state license number',
  },
  fields: {
    firstName: 'First name',
    firstNamePlaceholder: 'e.g. Jhon',
    lastName: 'Last name',
    lastNamePlaceholder: 'e.g. Doe',
    legalName: 'Legal name',
    agencyName: 'Agency name',
    agencyNamePlaceholder: 'Enter agency name',
    doingBusinessAs: 'Doing business as',
    agencyType: 'Agency type',
    agencyTypePlaceholder: 'Select agency type',
    registrationDate: 'Registration date',
    organizationWebsite: 'Organization website',
    organizationWebsitePlaceholder: 'Enter organization website',
    phone: 'Phone',
    phonePlaceholder: '+1 (XXX) XXX-XXXX',
    employerIdentificationNumber: 'Employer Identification Number (EIN)',
    stateLicenseNumber: 'State license number',
    statesOfService: 'States of service',

    primaryContactsFullName: 'Primary contact’s full name',
    primaryContactsFullNamePlaceholder: 'Enter primary contact’s full name',
    jobTitle: 'Job title',
    jobTitlePlaceholder: 'Enter job title',
    primaryPhoneNumber: 'Primary phone number',
    useOrganizationPhoneNumber: 'Use organization phone number',
    secondaryPhoneNumber: 'Secondary phone number',
    email: 'Email',
    emailPlaceholder: 'e.g. jhondoe@gmail.com',
    organizationsContactEmail: 'Enter organization’s contact email',

    addressLine1: 'Street address 1',
    addressLine1NoLocations: 'No locations',
    addressLine2: 'Street address 2',
    addressLinePlaceholder: 'Enter street name, number',
    cityAndState: 'City & State',
    zipCode: 'Zip code',

    weekStartDay: 'Working week start',
    linesOfBusiness: 'Lines of business of the organization',
    linesOfBusinessShort: 'Lines of business',
    linesOfBusinessPlaceholder: 'Select lines of business',
    jobQualifications: 'Provider Job Positions',
    jobQualificationsPlaceholder: 'Select positions that apply',
    qualificationsPlaceholder: 'Select the positions you staff',
    specialty: 'Specialty',

    talents: 'Providers',
    statuses: 'Statuses',

    minValueError: 'Minimum value is {value}',

    region: 'Region',
    regionHelper:
      'Region is the zone where your location operates. It is fetched from city and state indicated in location’s address',

    timezone: 'Timezone',
    timezonePlaceholder: 'Select a timezone',
  },
  shifts: {
    listPage: {
      filters: {
        startDate: {
          label: 'Start Date',
        },
        locations: {
          label: 'Locations',
        },
        qualifications: {
          label: 'Positions',
        },
        status: {
          label: 'Statuses',
        },
        talents: {
          label: 'Providers',
        },
      },
      table: {
        columns: {
          tags: {
            heading: 'Tags',
            emptyTag: 'No tags',
          },
        },
      },
    },
    drawers: {
      shiftDetails: {
        generalInfo: {
          tags: {
            heading: 'Tags',
          },
        },
      },
    },
    type: {
      shifts: 'Shifts',
      series: 'Series & Rotations',
    },
    recurrence: {
      single: 'Single',
      series: 'Series',
      rotation: 'Rotation',
    },
    status: {
      Assigned: 'Assigned',
      Reassigned: 'Re-assigned',
      Pending: 'Pending',
      InProgress: 'In progress',
      Completed: 'Completed',
      Canceled: 'Canceled',
      NotAllowed: 'N/A',
      Unfilled: 'Unfilled',
      assigned: 'Assigned',
      reassigned: 'Re-assigned',
      pending: 'Pending',
      inprogress: 'In progress',
      completed: 'Completed',
      canceled: 'Canceled',
      notallowed: 'N/A',
      unfilled: 'Unfilled',
    },
    shiftType: {
      day8: '8h Day',
      eve8: '8h Eve',
      noc8: '8h Noc',
      day12: '12h Day',
      noc12: '12h Noc',
    },
    table: {
      noRows: {
        title: 'No shifts yet',
        subtitle: 'You don’t have any shifts yet',
      },
    },
    fields: {
      shiftType: 'Shift type',
      recurrence: 'Recurrence',
      location: 'Location',
      qualification: 'Position',
      qualifications: 'Positions',
      specialty: 'Specialty',
      status: 'Status',
      notes: 'Notes',
      assignee: 'Assignee',
      startDate: 'Start Date',
      shiftDate: 'Shift date',
      sharedWithin: 'Shared within',
      cityAndState: 'City & State',
      locationName: 'Location name',
      address: 'Address',
      department: 'Department',
      subDepartment: 'Sub department',
      talents: 'Providers',
      isShared: 'Shared',
      series: 'Series',
      rotation: 'Rotation',
      assignmentType: 'Assignment type',
      search: 'Search',
      date: 'Date',
      assignedCount: {
        label: 'Assigned count',
        tooltip: '{assignedShiftCount} of {totalShiftCount} assigned',
      },
    },
    notNotes: 'No notes',
    externalIDPlaceholder: 'ID: {shiftId}',
    lengthAndType: '{length}h, {type}',
    length: '{length}h',
    emptyNote: 'There is no note related to the shift',
    assignBtnLabel: 'Assign',
    unassignBtnLabel: 'Unassign',
    assignMultipleBtnLabel: 'Assign recurrence',
    assignIndividualShiftsNotAllowedTooltip: 'Assignment of single shift is not allowed',
    assign: 'Assign',
    assignButton: {
      disabledTooltip: 'Assignee is not allowed for this status',
    },
    unassignButton: {
      disabledTooltip: "You can't unassign shift with this status",
    },
    menuItemUnassign: 'Unassign',
    menuItemReAssign: 'Re-assign',
    assignSuccessful: 'Shift has been assigned successfully',
    unassignSuccessful: 'Shift has been unassigned successfully',
    unassignDialog: {
      title: 'Unassign shift',
      subtitle: 'Please, specify the reason',
      reasonLabel: 'Reason of unassigning',
      reasonPlaceholder: 'Select the reason',
      shiftDetails: '{period}, {length} {type}, {qualifications}',
      reasonCommentLabel: 'What is the issue?',
      reasonCommentPlaceholder: 'Please, specify the reason',
      error: {
        InvalidUnassignAction: 'Impossible to unassign shift',
      },
      singleUnassignDisclaimer: 'The entire recurrence will be unassigned. To proceed click ‘Submit’',
    },
    assignDialog: {
      title: 'Assign shift',
      placeholder: 'Please, specify the provider',
      shiftDetails: '{period}, {length} {type}, {qualifications}',
      error: {
        InvalidAssignAction: 'This provider has another shift scheduled for this date',
      },
      singleAssigneeDisclaimer: 'This provider will be assigned to all the shifts included in this recurrence',
    },
    recurrent: {
      assignDialog: {
        title: 'Select a provider',
        shiftNumber: '{number} shifts',
        selectLabel: 'Assignee',
      },
    },
    startDateAll: 'All',
    shiftDetails: 'Shift details',
    locationDetails: 'Location details',
    mainScheduler: 'Main scheduler',
    afterHoursScheduler: 'After-hours scheduler',
    generalInfo: 'General info',
    notes: 'Notes',
    billing: 'Billing',
    dayOneInstructions: 'Day one instructions',
    additionalNotes: 'Additional notes',
    historyLog: 'History log',
    logs: {
      created: 'Shift created',
      shared: 'Shift shared',
      assigned: 'Shift assigned:',
      started: 'Shift started:',
      shiftTime: '{date}, {timeRange}, {shiftLength}h {shiftType}',
    },
    seriesAndRotations: {
      table: {
        header: {
          dates: 'Dates',
          id: 'ID',
          duration: 'Duration',
        },
      },
      copyId: 'Copy ID',
      durationWeeks: '{count, plural, one {# week} other {# weeks}}',
      durationDays: '{count, plural, one {# day} other {# days}}',
      externalIDPlaceholder: '{shiftId}',
      tooltip: 'Copy shift ID to clipboard',
    },
    shiftsTable: 'Shifts {number, select, 0 {} other {({number})}}',
    noAssignees: 'No assignees',
    editSeriesDrawer: {
      selectedShifts: '{number} shifts selected',
    },
  },
  billing: {
    bonusRate: 'Bonus rate',
    rate: {
      perHour: 'Per hour',
      perShift: 'Per shift',
    },
    rates: 'Rates',
    totals: 'Totals',
    billRate: 'Bill rate',
    payRate: 'Pay rate',
    bonusAmount: 'Bonus amount',
    billAmount: 'Bill amount',
    payAmount: 'Pay amount',
    grossMargin: 'Gross margin',
    totalTime: 'Total time',
  },
  filters: {
    dateRange: {
      previousQuarter: 'Previous quarter',
      previousWeek: 'Previous week',
      today: 'Today',
      thisWeek: 'This week',
      thisQuarter: 'This quarter',
      nextWeek: 'Next week',
      nextQuarter: 'Next quarter',
    },
    sharedWithin: {
      menuLabel: 'Shared within',
      last24Hours: 'Last 24 hours',
      last48Hours: 'Last 48 hours',
      allTime: 'All time',
      last7Days: 'Last 7 days',
      last30Days: 'Last 30 days',
    },
  },
  staff: {
    menu: {
      actions: {
        menuButton: {
          label: 'Actions',
        },
        options: {
          activateUsers: {
            label: 'Activate',
          },
          deactivateUsers: {
            label: 'Deactivate',
          },
          sendInvites: {
            label: 'Send invite(s)',
          },
          resendInvites: {
            label: 'Resend invite(s)',
          },
          messages: {
            deactivateSuccess:
              '{count, plural, one {Provider has} other {# providers have}} been deactivated successfully',
            activateSuccess: '{count, plural, one {Provider has} other {# providers have}} been activated successfully',
          },
        },
      },
      plusButton: {
        menuButton: {
          label: 'Add Provider',
        },
        options: {
          addSingle: {
            label: 'Individual provider',
          },
          csvImport: {
            label: 'CSV import',
          },
        },
      },
    },
    filters: {
      qualifications: {
        label: 'Positions',
        options: {
          All: 'All',
        },
        buttons: {
          clear: { label: 'Clear' },
          apply: { label: 'Apply' },
        },
      },
      specialties: {
        label: 'Specialties',
        options: {
          All: 'All',
        },
        buttons: {
          clear: { label: 'Clear' },
          apply: { label: 'Apply' },
        },
      },
      status: {
        label: 'Status',
        options: {
          All: 'All',
          Active: 'Active',
          Deactivated: 'Deactivated',
        },
        buttons: {
          clear: { label: 'Clear' },
          apply: { label: 'Apply' },
        },
      },
      search: {
        placeholder: 'Search...',
        label: 'Search',
      },
      clearAllFilters: {
        label: 'Clear all filters',
      },
    },
    table: {
      columns: {
        name: {
          heading: 'Name',
        },
        qualifications: {
          heading: 'Positions',
        },
        email: {
          heading: 'Email',
          noEmail: {
            label: 'No email specified',
            tooltip:
              'Current provider specified with no email. Please consider to use provider edit to add email to the provider',
          },
        },
        phone: {
          heading: 'Phone',
          noPhone: {
            label: 'No phone number specified',
            tooltip:
              'Current provider specified with no phone number. Please consider to use provider edit to add phone number to the provider',
          },
        },
        specialties: {
          heading: 'Specialties',
        },
        lastWorkedTime: {
          heading: 'Last Worked',
        },
        status: {
          heading: 'Status',
          options: {
            Active: 'Active',
            Deactivated: 'Deactivated',
            Deleted: 'Deleted',
            InviteFailed: 'Invite failed',
            Invited: 'Invited',
            Pending: 'Pending',
          },
        },
        controls: {
          view: {
            label: 'Edit',
          },
          deactivate: {
            label: 'Deactivate',
            successMessage:
              '{count, plural, one {Provider has} other {# providers have}} been deactivated successfully',
          },
          activate: {
            label: 'Activate',
            successMessage: '{count, plural, one {Provider has} other {# providers have}} been activated successfully',
          },
        },
      },
      noRows: {
        title: 'No providers yet',
        subtitle: 'You don’t have any providers yet',
      },
      noFilteredRows: {
        title: 'No results',
        subtitle: 'No results matching your request',
      },
      loading: {
        title: 'Loading...',
        subtitle: 'Please wait a moment',
      },
    },
    errors: {
      talentsHaveActiveShiftsError: `{count, plural, one {Provider {items} has} other {Providers {items} have}} active shifts. Please unassign them before deactivating {count, plural, one {the provider} other {providers}}`,
      talentHasActiveShiftsError:
        'You cannot deactivate provider with scheduled shifts. To proceed, reassign their shifts to another provider before attempting deactivation',
    },
  },
  talents: {
    listPage: {
      table: {
        errors: {
          noPhone: {
            label: 'No phone number',
            tooltip:
              'Current provider specified with no phone number. Please consider to use provider edit to add phone number to the provider',
          },
          noEmail: {
            label: 'No email specified',
            tooltip:
              'Current provider specified with no email. Please consider to use provider edit to add email to the provider',
          },
        },
        noRows: {
          title: 'No providers yet',
          subtitle: 'You don’t have any providers yet',
        },
        filteredNoRows: {
          title: 'No results',
          subtitle: 'No results matching your request',
        },
      },
    },
    drawers: {
      editTalent: {
        tabs: {
          general: {
            label: 'General',
          },
          roleAndPay: {
            label: 'Role & Pay rate',
          },
          availability: {
            label: 'Availability',
          },
          credentials: {
            label: 'Credentials',
            noRows: {
              title: 'No credentials yet',
              subtitle: 'Provider has no credentials yet',
            },
            addNew: {
              heading: 'Add credential',
            },
            types: {
              License: 'License',
              Certification: 'Certification',
              BackgroundCheck: 'Background check',
              HealthRecord: 'Health record',
              Assessment: 'Assessment',
              EDocument: 'E-document',
            },
          },
        },
        availability: {
          employmentType: {
            label: 'Employment Type',
            options: {
              Contract: 'Contract',
              SalariedPartTime: 'Salaried Part Time',
              SalariedFullTime: 'Salaried Full Time',
              HourlyPartTime: 'Hourly part time',
              HourlyFullTime: 'Hourly full time',
              PerDiemOrPRN: 'Per diem or PRN',
            },
          },
        },
      },
    },
    labelOne: 'Provider',
    addButtonLabel: 'Add provider',
    addMenu: {
      addSingle: 'Individual provider',
      csvImport: 'CSV import',
    },
    qualifications: 'Positions',
    lastWorked: 'Last worked',
    statuses: {
      active: 'Active',
      deactivated: 'Deactivated',
      deleted: 'Deleted',
      invitefailed: 'Invite failed',
      invited: 'Invited',
      pending: 'Pending',
    },
    qualification: 'Position',
    specialty: 'Specialty',
    editForm: {
      header: 'Add internal provider',
      editHeader: 'Edit internal provider',
      personalInformation: 'Personal Information',
      profileImageTitle: 'Upload profile image',
      profileImageDisclaimer: 'Image should be PNG or JPG format, 2MB max.',
      profileImageSubmitTitle: 'Upload image',
      contactInformation: 'Contact Information',
      hrInformation: 'HR Information',
      primaryRegion: 'Primary region',
      secondaryRegion: 'Secondary region',
      roleAndPosition: 'Location and job title',
      qualificationAndSpecialties: 'Position and specialties',
      specialtiesApplied: 'Specialties applied',
      specialties: 'Specialties',
      rateType: 'Rate type',
      baseRate: 'Base rate',
      deleteQualification: 'Delete position',
      addQualification: 'Add position',
      deleteSpecialty: 'Delete specialty',
      addSpecialty: 'Add specialty',
      travel: 'Travel',
      availableForTravel: 'Available for travel',
      schedule: 'Schedule',
      success: 'Provider has been created successfully',
      successEdit: 'Provider has been edited successfully',
      maxTravelDistance: 'Max. travel distance (in miles)',
      stepGeneral: 'General',
      stepRoleAndPay: 'Role & Pay rate',
      stepAvailability: 'Availability',
      stepPositionAndPayRates: 'Position and pay rates',
      minShiftsValidationError: 'At least one shift is required',
      errors: {
        DuplicateEmail: 'User with this email already exists',
      },
      contractDetails: 'Contract details',
    },
    rateTypes: {
      hourly: 'Hourly',
      monthly: 'Monthly',
      yearly: 'Yearly',
    },
    table: {},
    // deactivateUser: 'Deactivate',
    // deactivateUsers: 'Deactivate',
    // activateUser: 'Activate',
    // activateUsers: 'Activate',
    // talentsHaveActiveShiftsError: `{count, plural, one {Provider {items} has} other {Providers {items} have}} active shifts. Please unassign them before deactivating {count, plural, one {the provider} other {providers}}`,
    // talentHasActiveShiftsError:
    //   'You cannot deactivate provider with scheduled shifts. To proceed, reassign their shifts to another provider before attempting deactivation',
    // sendInvites: 'Send invite(s)',
    // resendInvites: 'Resend invite(s)',
    staffingType: {
      label: 'Staffing type',
      internal: 'Internal',
      agency: 'Agency',
      directContractor: 'Direct contractor',
    },
    fields: {
      specialities: 'Specialities',
      nurseQualifications: 'Positions',
      contractType: 'Contract type',
    },

    contractTypes: {
      w2: 'W2',
      1099: '1099',
      w4: 'W4',
      c2c: 'C2C',
      c2h: 'C2H',
      c2p: 'C2P',
    },
    deactivateSuccess: '{count, plural, one {Provider has} other {# providers have}} been deactivated successfully',
    activateSuccess: '{count, plural, one {Provider has} other {# providers have}} been activated successfully',
    employmentType: {
      label: 'Employment type',
      Contract: 'Contract',
      SalariedPartTime: 'Salaried Part Time',
      SalariedFullTime: 'Salaried Full Time',
      HourlyPartTime: 'Hourly Part Time',
      HourlyFullTime: 'Hourly Full Time',
      PerDiemOrPRN: 'Per Diem or PRN',
    },
  },
  clients: {
    viewSwitcher: {
      clients: 'Clients',
      locations: 'Locations',
    },
    clients: {
      filters: {
        managers: {
          label: 'Managers',
          options: {
            All: 'All',
          },
          buttons: {
            clear: {
              label: 'Clear',
            },
            apply: {
              label: 'Apply filters',
            },
          },
        },
        relationStatuses: {
          label: 'Status',
          options: {
            Active: 'Active',
            Pending: 'Pending',
            OnReview: 'On Review',
            Disconnected: 'Disconnected',
            All: 'All',
          },
          buttons: {
            clear: {
              label: 'Clear',
            },
            apply: {
              label: 'Apply filters',
            },
          },
        },
        regions: {
          label: 'Regions',
          options: {
            All: 'All',
          },
          buttons: {
            clear: {
              label: 'Clear',
            },
            apply: {
              label: 'Apply filters',
            },
          },
        },
        search: {
          placeholder: 'Search...',
          label: 'Search',
        },
        clearAllFilters: {
          label: 'Clear all filters',
        },
      },
      table: {
        fields: {
          name: {
            heading: 'Client name',
            locationsNumber: '{number, plural, =0 {-} one {# location} other {# locations} }',
          },
          contractType: {
            heading: 'Contract',
            values: {
              Direct: 'Direct',
              Managed: 'Managed',
            },
          },
          regions: {
            heading: 'Regions',
          },
          openShifts: {
            heading: 'Open Shifts',
          },
          completedShifts: {
            heading: 'Completed shifts',
          },
          inviteStatus: {
            heading: 'Invite status',
            values: {
              Active: 'Active',
              Pending: 'Pending',
              OnReview: 'On Review',
              Disconnected: 'Disconnected',
            },
          },
          manager: {
            heading: 'Manager',
            assignBtn: {
              label: 'Assign manager',
            },
          },
        },
        noRows: {
          title: 'No clients yet',
          subtitle: 'You don’t have any client’s requests yet',
        },
        noFilteredRows: {
          title: 'No results',
          subtitle: 'No results matching your request',
        },
      },
    },
    locations: {
      filters: {
        managers: {
          label: 'Managers',
          options: {
            All: 'All',
          },
          buttons: {
            clear: {
              label: 'Clear',
            },
            apply: {
              label: 'Apply filters',
            },
          },
        },
        regions: {
          label: 'Regions',
          options: {
            All: 'All',
          },
          buttons: {
            clear: {
              label: 'Clear',
            },
            apply: {
              label: 'Apply filters',
            },
          },
        },
        search: {
          placeholder: 'Search...',
          label: 'Search',
        },
        clearAllFilters: {
          label: 'Clear all filters',
        },
      },
      table: {
        fields: {
          name: {
            heading: 'Name',
          },
          regions: {
            heading: 'Regions',
          },
          openShifts: {
            heading: 'Open Shifts',
          },
          completedShifts: {
            heading: 'Completed shifts',
          },
          manager: {
            heading: 'Manager',
            assignBtn: {
              label: 'Assign manager',
            },
          },
        },
        noRows: {
          title: 'No locations yet',
          subtitle: 'No locations yet',
        },
        noFilteredRows: {
          title: 'No results',
          subtitle: 'No results matching your request',
        },
      },
    },
    manager: 'Manager',
    managers: 'Managers',
    assignManager: 'Assign manager',
  },
  users: {
    status: {
      InviteFailed: 'Invite failed',
      Invited: 'Invited',
      Pending: 'Pending',
      Active: 'Active',
      Deactivated: 'Deactivated',
    },
    usersCount: '{number, plural, =0 {No users} one {# user} other {# users} }',
    table: {
      noRows: {
        title: 'No users yet',
        subtitle: 'You don’t have any users yet added',
      },
    },
    addUserButtonLabel: 'Add user',
    userForm: {
      title: 'Add system user',
      editTitle: 'Edit system user',
      stepGeneral: 'General',
      stepEmployment: 'Employment & Positions',
      success: 'User has been created successfully',
      submitNew: {
        label: 'Invite',
      },
    },
    error: {
      409: 'User with this email already exists',
      image: {
        InvalidSize: 'Image size is invalid',
      },
    },
  },
  linesOfBusiness: {
    AcuteCare: 'Acute Care',
    AmbulatoryCare: 'Ambulatory Care',
    LongTermCare: 'Long-Term Care',
    BehavioralHealth: 'Behavioral Health',
    RehabilitationServices: 'Rehabilitation Services',
    HomeHealthCare: 'Home Health Care',
    DiagnosticServices: 'Diagnostic Services',
    PreventiveAndWellnessServices: 'Preventive and Wellness Services',
    PharmacyServices: 'Pharmacy Services',
    TelehealthAndTelemedicine: 'Telehealth and Telemedicine',
    AncillaryServices: 'Ancillary Services',
    DentalServices: 'Dental Services',
    VisionServices: 'Vision Services',
    PublicHealthServices: 'Public Health Services',
    ResearchAndEducation: 'Research and Education',
  },
  locations: {
    status: {
      Active: {
        tableChip: {
          label: ' Active',
        },
      },
      Deactivated: {
        tableChip: {
          label: 'Deactivated',
        },
      },
    },
  },
  timecards: {
    drawers: {
      edit: {
        dialogHeading: 'Timecard details',
        externalIDPlaceholder: 'ID: {shiftId}',
        status: {
          tooltip: 'Timecard status',
        },
        menuButtons: {
          cancel: {
            label: 'Back',
          },
          save: {
            label: 'Save',
            tooltip:
              'If you do not submit the data for approval and this timecard will be edited by your client and sent to you, all previously entered data will be overwritten and lost',
          },
          approve: {
            label: 'Approve',
            tooltip: 'Approve timecard',
            tooltipSave: 'Save changes and approve timecard',
          },
          decline: {
            label: 'Decline',
            tooltip: 'Decline current timecard',
          },
          submitForApproval: {
            label: 'Send for approval',
            tooltip: 'Submit timecard for agency approval',
            tooltipSave: 'Save changes and submit timecard for agency approval',
          },
          resubmitForApproval: {
            label: 'Resend for approval',
            tooltip: 'Resubmit timecard for agency approval',
            tooltipSave: 'Save changes and resubmit timecard for agency approval',
          },
          appealAndResubmit: {
            label: 'Appeal',
            tooltip: 'Appeal the timecard and resend for approval',
          },
        },
        shiftDetails: {
          scheduled: 'Scheduled - {locationName}',
          date: '{fromDate} - {toTime}, {durationHours}h',
          employmentType: {
            internal: {
              label: 'Internal',
            },
            external: {
              label: 'External',
            },
          },
        },
        timekeeping: {
          sectHeading: 'Timekeeping',
        },
        timesection: {
          startDate: {
            label: 'Clock-in time',
            helper: 'Scheduled: {scheduledTime}',
          },
          endDate: {
            label: 'Clock-out time',
            helper: 'Scheduled: {scheduledTime}',
            errors: {
              lessThanStart: 'Clock-out time should be greater than clock-in time',
            },
          },
          mealBreak: {
            label: 'Meal break duration',
            helper: 'Scheduled break duration: 30 min ',
            errors: {
              greaterThanShift: 'Meal break cannot exceed total shift duration',
            },
          },
          total: {
            label: 'Total duration: ',
            value: '{durationHours}h',
          },
        },
        notes: {
          sectHeading: 'Notes',
          subSectHeading: 'General notes',
          notesInput: {
            placeholder: 'no notes added',
          },
        },
        totalSection: {
          duration: {
            heading: {
              label: 'Total duration',
            },
          },
          overtime: {
            heading: {
              label: 'Overtime',
            },
            value: '{durationHours}h',
          },
          totalPay: {
            heading: {
              label: 'Total pay',
            },
            value: '{amount} USD',
          },
          totalBill: {
            heading: {
              label: 'Total bill',
            },
            value: '{amount} USD',
          },
        },
        externalTabs: {
          general: {
            label: 'General details',
          },
          interactionLog: {
            label: 'Interaction log',
          },
        },
        interactionLog: {
          Decline: {
            heading: {
              label: 'Declined by {orgName}',
            },
            attachmentBadge: {
              label: 'View attachment',
            },
            reason: {
              heading: 'Reason of decline',
            },
            note: {
              heading: 'Note',
            },
          },
        },
      },
      decline: {
        dialogHeading: 'Decline timecard',
        externalIDPlaceholder: 'ID: {shiftId}',
        menuButtons: {
          cancel: {
            label: 'Back',
          },
          decline: {
            label: 'Decline',
            tooltip: 'Decline current timecard',
          },
        },
        reason: {
          label: 'Select the reason',
          placeholder: 'Reason',
        },
        note: {
          label: 'Note',
          placeholder: 'Please, add a note',
        },
        attachment: {
          label: 'Attachment',
        },
      },
      appeal: {
        dialogHeading: 'Appeal timecard',
        externalIDPlaceholder: 'ID: {shiftId}',
        menuButtons: {
          cancel: {
            label: 'Back',
          },
          appeal: {
            label: 'Appeal',
            tooltip: 'Appeal timecard and resend for approval',
          },
        },
        reason: {
          label: 'Select the reason',
          placeholder: 'Reason',
        },
        note: {
          label: 'Note',
          placeholder: 'Please, add a note',
        },
        attachment: {
          label: 'Attachment',
        },
      },
      InteractionLog: {
        declineSection: {
          orgName: 'Declined by {orgName}',
          viewAttachment: {
            label: 'View attachment',
          },
          reason: {
            subHeading: 'Reason of decline',
            IncorrectData: 'Incorrect data',
            ProviderDidNotWorkTheShift: 'Provider didn’t work the shift',
          },
          note: {
            subHeading: 'Note',
          },
        },
        appealSection: {
          orgName: 'Appealed by {orgName}',
          viewAttachment: {
            label: 'View attachment',
          },
          reason: {
            subHeading: 'Reason of decline',
            IncorrectData: 'Incorrect data',
            ProviderDidNotWorkTheShift: 'Provider didn’t work the shift',
          },
          note: {
            subHeading: 'Note',
          },
        },
      },
    },
    listPage: {
      title: 'Timecards',
      filters: {
        startDate: {
          label: 'Dates',
          allOption: 'All',
        },
        staff: {
          label: 'Providers',
        },
        locations: {
          label: 'Locations',
        },
        nurseQualifications: {
          label: 'Positions',
        },
        specialties: {
          label: 'Specialties',
        },
        search: {
          label: 'Search',
        },
        statuses: {
          label: 'Status',
        },
        type: {
          label: 'Staffing type',
          options: {
            all: 'All',
            internal: 'Internal',
            external: 'External',
          },
        },
      },
      table: {
        fields: {
          externalId: {
            heading: 'Shift ID',
            externalIDPlaceholder: 'Copy',
          },
          plannedStartDate: {
            heading: 'Scheduled time',
          },
          actualTimeRange: {
            heading: 'Actual time',
          },
          staffMember: {
            heading: 'Provider',
          },
          chosenNurseQualifications: {
            heading: 'Positions',
          },
          location: {
            heading: 'Location',
          },
          status: {
            heading: 'Status',
          },
        },
        noRows: {
          title: 'No records found',
          subtitle: 'You don’t have any timecards yet',
        },
      },
    },
    status: {
      draft: 'Draft',
      pending: 'Pending',
      declined: 'Declined',
      toreview: 'To Review',
      returned: 'Returned',
      approved: 'Approved',
      tooltip: 'Timecard status',
    },
    errors: {
      TimecardNotDraft:
        'If you do not submit the data for approval and this timecard will be edited by your client and sent to you, all previously entered data will be overwritten and lost',
    },
  },
});
